/**
 * Periodontograma (Especialidades → Periodoncia): registro de los seis sitios de cada diente con profundidad de sondaje, recesión,
 * sangrado, placa y supuración, más movilidad y furca. Se guarda cada vez como un registro nuevo e inalterable y se compara con el anterior.
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { api, puede, type Paciente } from "../api.ts";
import {
  DIENTES_SUPERIORES, DIENTES_INFERIORES, periodontogramaVacio, limpiarPeriodontograma, resumenPeriodontograma, textoResumenPerio, compararPerio, problemasPeriodontograma, tieneFurca,
  type DatosPerio, type SitioPerio, type ResumenPerio,
} from "../../../dominio/src/index.ts";
import { AvisoError, Seccion, useAccion } from "./comun.tsx";
import { FechaInput } from "../componentes/CampoFecha.tsx";
import { hoy } from "./comun.tsx";

interface Registro { id: string; fecha: string; datos: DatosPerio; resumen: ResumenPerio; observaciones: string; profesional: string }

const fechaLarga = (iso: string) => new Date(iso + "T12:00:00").toLocaleDateString("es-EC", { day: "numeric", month: "short", year: "numeric" });
const signo = (n: number | null, unidad: string) => (n === null ? "" : n === 0 ? `sin cambio en ${unidad}` : `${n < 0 ? "▼" : "▲"} ${n > 0 ? "+" : "−"}${Math.abs(n)} ${unidad}`);

/** Casilla numérica de una medida: acepta vacío (sin medir) y, si se permite, el signo menos. Enter pasa a la siguiente. */
function Num({ valor, alCambiar, min, max, etiqueta, bloqueado, clase }: { valor: number | null; alCambiar: (n: number | null) => void; min: number; max: number; etiqueta: string; bloqueado: boolean; clase?: string }) {
  const leer = (t: string): number | null => { const n = Number(t); return t.trim() === "" || !/^-?\d{1,2}$/.test(t.trim()) || n < min || n > max ? null : n; };
  const [t, setT] = useState(valor === null ? "" : String(valor));
  useEffect(() => { if (leer(t) !== valor) setT(valor === null ? "" : String(valor)); }, [valor]);
  return (
    <input className={`perio-n ${clase ?? ""}`} inputMode="numeric" maxLength={min < 0 ? 3 : 2} aria-label={etiqueta} value={t} disabled={bloqueado}
      onChange={(e) => { const x = e.target.value; if (x === "" || /^-?\d{0,2}$/.test(x)) { setT(x); alCambiar(leer(x)); } }}
      onFocus={(e) => e.target.select()}
      onKeyDown={(e) => {
        // Enter sube el valor de uno en uno; al pasar del máximo vuelve a 0. Con Shift se pasa a la casilla siguiente (lo atiende la tabla).
        if (e.key !== "Enter") return;
        e.preventDefault();
        const sig = (valor ?? 0) + 1;
        const n = sig > max ? 0 : sig;
        setT(String(n)); alCambiar(n);
      }} />
  );
}

const Marca = ({ activa, alCambiar, etiqueta, bloqueado, clase }: { activa: boolean; alCambiar: () => void; etiqueta: string; bloqueado: boolean; clase: string }) => (
  <button type="button" className={`perio-m ${clase}${activa ? " on" : ""}`} aria-pressed={activa} aria-label={etiqueta} disabled={bloqueado} onClick={alCambiar}><span className="perio-pt" /></button>
);

const NOMBRE_SITIO = ["mesial", "central", "distal"];

const VERDE = "#2e9e5b", MARRON = "#8b5a2b", ROJO = "#d13b3b", MARGEN = "#6b7280";
/** Color de un sitio: rojo = supuración o sondaje de 6 mm o más; marrón = sangrado o sondaje de 4 a 5 mm; verde = sin hallazgo. */
const colorSitio = (s: SitioPerio) => (s.sup || (s.pd ?? 0) >= 6 ? ROJO : s.sang || (s.pd ?? 0) >= 4 ? MARRON : VERDE);

/** Ancho de cada sitio (px) y medidas del dibujo de un diente. Todas las columnas de la tabla miden igual para que el dibujo calce con las casillas. */
const W = 26, ETIQ = 100, H = 120, CEJ = 68, ESC = 4;

/** En los dientes del lado derecho del paciente (cuadrantes 1 y 4, a la izquierda de la pantalla) lo mesial queda a la derecha. */
const ordenSitios = (n: number): number[] => (n < 20 || (n > 40 && n < 50) ? [2, 1, 0] : [0, 1, 2]);

const RAIZ_PIEL = "#e8d5b5", CORONA = "#ffffff", TRAZO = "#5b5b5b";
/** Dibujo sencillo de cada tipo de diente (caja de 60 × 120, raíz arriba, corona abajo; el límite corona-raíz queda en y = 68). */
function Forma({ n }: { n: number }) {
  const t = n % 10, sup = n < 30;
  const corona = t <= 2 ? "M14 68 C12 82 11 100 14 114 Q30 119 46 114 C49 100 48 82 46 68 Z"
    : t === 3 ? "M13 68 C11 84 13 100 18 108 L30 119 L42 108 C47 100 49 84 47 68 Z"
    : t <= 5 ? "M12 68 C10 84 12 100 16 110 Q30 118 44 110 C48 100 50 84 48 68 Z"
    : "M8 68 C5 86 6 104 12 112 Q30 120 48 112 C54 104 55 86 52 68 Z";
  const raices = t <= 2 ? ["M18 68 C20 40 26 14 30 4 C34 14 40 40 42 68 Z"]
    : t === 3 ? ["M18 68 C20 36 27 10 30 2 C33 10 40 36 42 68 Z"]
    : t <= 5 ? ["M17 68 C19 40 24 14 28 6 C32 16 38 40 43 68 Z"]
    : sup ? ["M10 68 C9 48 11 26 15 8 C19 26 24 48 24 68 Z", "M24 68 C26 44 28 20 30 10 C32 20 34 44 36 68 Z", "M36 68 C36 48 41 26 45 8 C49 26 51 48 50 68 Z"]
    : ["M9 68 C8 44 10 22 14 6 C20 26 26 48 30 68 Z", "M30 68 C34 48 40 26 46 6 C50 22 52 44 51 68 Z"];
  return (
    <g stroke={TRAZO} strokeWidth="0.9" strokeLinejoin="round">
      {raices.map((d) => <path key={d} d={d} fill={RAIZ_PIEL} />)}
      <path d={corona} fill={CORONA} />
    </g>
  );
}

/**
 * Una cara de la arcada dibujada sobre los dientes: línea gris = margen de la encía (recesión) y línea de color = fondo del surco (margen + sondaje),
 * verde sin hallazgo, marrón con sangrado o 4 a 5 mm, roja con supuración o 6 mm o más. «abajo» refleja el dibujo (raíces hacia abajo).
 */
function DientesCara({ dientes, datos, cara, abajo, nombre }: { dientes: readonly number[]; datos: DatosPerio; cara: "v" | "l"; abajo: boolean; nombre: string }) {
  const ancho = dientes.length * 3 * W;
  const py = (v: number) => { const c = Math.max(2, Math.min(H - 2, v)); return abajo ? H - c : c; };
  type P = { g: number; x: number; s: SitioPerio; n: number };
  const puntos: P[] = dientes.flatMap((n, ti) => (datos[n].ausente ? [] : ordenSitios(n).map((i, p) => ({ g: ti * 3 + p, x: (ti * 3 + p + 0.5) * W, s: datos[n][cara][i], n }))));
  const margen = puntos.filter((p) => p.s.pd !== null || p.s.rec !== null);
  const surco = puntos.filter((p) => p.s.pd !== null);
  const yMargen = (p: P) => py(CEJ - (p.s.rec ?? 0) * ESC);
  const ySurco = (p: P) => py(CEJ - ((p.s.rec ?? 0) + (p.s.pd ?? 0)) * ESC);
  const unidos = (l: P[]) => l.slice(1).map((b, j) => ({ a: l[j], b })).filter(({ a, b }) => b.g - a.g === 1);
  return (
    <svg width={ancho} height={H} role="img" aria-label={`Dibujo de los dientes, cara ${nombre}`} style={{ display: "block" }}>
      {dientes.map((n, ti) => (
        <g key={n} transform={`translate(${ti * 3 * W + (3 * W - 60) / 2},0)`} opacity={datos[n].ausente ? 0.2 : 1}>
          <g transform={abajo ? `translate(0,${H}) scale(1,-1)` : undefined}><Forma n={n} /></g>
        </g>
      ))}
      {unidos(margen).map(({ a, b }) => <line key={`m${a.g}`} x1={a.x} y1={yMargen(a)} x2={b.x} y2={yMargen(b)} stroke={MARGEN} strokeWidth="1.6" strokeLinecap="round" />)}
      {unidos(surco).map(({ a, b }) => <line key={`s${a.g}`} x1={a.x} y1={ySurco(a)} x2={b.x} y2={ySurco(b)} stroke={colorSitio(b.s)} strokeWidth="2.6" strokeLinecap="round" />)}
      {margen.map((p) => <circle key={`pm${p.g}`} cx={p.x} cy={yMargen(p)} r="2.2" fill={MARGEN} />)}
      {surco.map((p) => (
        <circle key={`ps${p.g}`} cx={p.x} cy={ySurco(p)} r="3.4" fill={colorSitio(p.s)} stroke="#fff" strokeWidth="1">
          <title>{`Diente ${p.n}: sondaje ${p.s.pd} mm${p.s.rec ? ` · recesión ${p.s.rec} mm` : ""}${p.s.sang ? " · sangra" : ""}${p.s.sup ? " · supuración" : ""}`}</title>
        </circle>
      ))}
    </svg>
  );
}

export function Arcada({ dientes, datos, cambiar, bloqueado, inferior }: { dientes: readonly number[]; datos: DatosPerio; cambiar: (n: number, f: (d: DatosPerio[string]) => DatosPerio[string]) => void; bloqueado: boolean; inferior: boolean }) {
  const caras: ["v" | "l", string][] = inferior ? [["l", "Lingual"], ["v", "Vestibular"]] : [["v", "Vestibular"], ["l", "Palatino"]];
  const [primera, segunda] = caras;
  const sitio = (n: number, cara: "v" | "l", i: number, f: (s: SitioPerio) => SitioPerio) => cambiar(n, (d) => ({ ...d, [cara]: d[cara].map((s, j) => (j === i ? f(s) : s)) }));
  const cols = dientes.length * 3;
  const celdas = (clave: string, f: (n: number, i: number) => ReactNode) =>
    dientes.flatMap((n) => ordenSitios(n).map((i) => <td key={`${clave}${n}${i}`} className={datos[n].ausente ? "perio-aus" : ""}>{!datos[n].ausente && f(n, i)}</td>));
  const fila = (clave: string, titulo: string, f: (n: number, i: number) => ReactNode, ayuda?: string) => (
    <tr key={clave}><th scope="row" className="perio-et" title={ayuda}>{titulo}</th>{celdas(clave, f)}</tr>
  );
  const marca = ([cara, nombre]: ["v" | "l", string], campo: "sang" | "placa" | "sup", titulo: string) =>
    fila(`${cara}${campo}`, titulo, (n, i) => <Marca clase={campo} activa={datos[n][cara][i][campo]} bloqueado={bloqueado} etiqueta={`${titulo} ${n} ${nombre} ${NOMBRE_SITIO[i]}`} alCambiar={() => sitio(n, cara, i, (s) => ({ ...s, [campo]: !s[campo] }))} />);
  const sondaje = ([cara, nombre]: ["v" | "l", string]) =>
    fila(`${cara}pd`, "Profundidad de sondaje", (n, i) => { const s = datos[n][cara][i]; return <Num valor={s.pd} min={0} max={15} bloqueado={bloqueado} clase={s.pd !== null && s.pd >= 6 ? "grave" : s.pd !== null && s.pd >= 4 ? "alto" : ""} etiqueta={`Sondaje ${n} ${nombre} ${NOMBRE_SITIO[i]}`} alCambiar={(x) => sitio(n, cara, i, (y) => ({ ...y, pd: x }))} />; });
  const recesion = ([cara, nombre]: ["v" | "l", string]) =>
    fila(`${cara}rec`, "Recesión", (n, i) => <Num valor={datos[n][cara][i].rec} min={-5} max={15} bloqueado={bloqueado} etiqueta={`Recesión ${n} ${nombre} ${NOMBRE_SITIO[i]}`} alCambiar={(x) => sitio(n, cara, i, (s) => ({ ...s, rec: x }))} />,
      "Recesión en mm: positivo = el margen está por debajo del límite amelocementario; negativo = por encima");
  const porDiente = (clave: string, titulo: string, f: (n: number) => ReactNode) => (
    <tr key={clave}><th scope="row" className="perio-et">{titulo}</th>{dientes.map((n) => <td key={n} colSpan={3} className={datos[n].ausente ? "perio-aus" : ""}>{!datos[n].ausente && f(n)}</td>)}</tr>
  );
  const movilidad = porDiente("mov", "Movilidad", (n) => <button type="button" className="perio-ciclo" disabled={bloqueado} aria-label={`Movilidad del ${n}`} onClick={() => cambiar(n, (d) => ({ ...d, mov: (d.mov + 1) % 4 }))}>{datos[n].mov || "0"}</button>);
  const furca = porDiente("furca", "Furcación", (n) => tieneFurca(n) && <button type="button" className="perio-ciclo" disabled={bloqueado} aria-label={`Furca del ${n}`} onClick={() => cambiar(n, (d) => ({ ...d, furca: (d.furca + 1) % 4 }))}>{datos[n].furca || "0"}</button>);
  const imagen = (clave: string, cara: "v" | "l", nombre: string, abajo: boolean) => (
    <tr key={clave} className="perio-img"><th scope="row" className="perio-et perio-et-img">{nombre}</th>
      <td colSpan={cols}><DientesCara dientes={dientes} datos={datos} cara={cara} abajo={abajo} nombre={nombre} /></td></tr>
  );
  const numeros = (
    <tr key="num" className="perio-nums"><th scope="row" className="perio-et">Diente</th>
      {dientes.map((n) => (
        <td key={n} colSpan={3} className={datos[n].ausente ? "perio-aus" : ""}>
          <button type="button" className="perio-dte" disabled={bloqueado} aria-pressed={datos[n].ausente} title={datos[n].ausente ? "Ausente: toca para marcar presente" : "Toca para marcar ausente"} onClick={() => cambiar(n, (d) => ({ ...d, ausente: !d.ausente }))}>{n}</button>
        </td>
      ))}</tr>
  );
  // De afuera hacia los dientes: sangrado, placa, supuración, (movilidad), furcación, sondaje, recesión; del otro lado, al revés.
  const arriba = [marca(primera, "sang", "Sangrado"), marca(primera, "placa", "Placa"), marca(primera, "sup", "Supuración"), ...(inferior ? [] : [movilidad]), furca, sondaje(primera), recesion(primera)];
  const abajoFilas = [recesion(segunda), sondaje(segunda), ...(inferior ? [movilidad] : []), marca(segunda, "sup", "Supuración"), marca(segunda, "placa", "Placa"), marca(segunda, "sang", "Sangrado")];
  return (
    <div className="perio-scroll">
      <table className="perio-tabla" style={{ width: ETIQ + cols * W }} onKeyDown={(e) => {
        if (e.key !== "Shift" || e.repeat) return;
        const todas = [...document.querySelectorAll<HTMLElement>(".perio-tabla input.perio-n:not(:disabled), .perio-tabla button.perio-m:not(:disabled), .perio-tabla button.perio-ciclo:not(:disabled)")];
        const i = todas.indexOf(e.target as HTMLElement);
        if (i >= 0) { e.preventDefault(); todas[i + 1]?.focus(); }
      }}>
        <colgroup><col style={{ width: ETIQ }} />{Array.from({ length: cols }, (_, i) => <col key={i} style={{ width: W }} />)}</colgroup>
        <tbody>
          {arriba}
          {imagen("img1", primera[0], primera[1], false)}
          {numeros}
          {imagen("img2", segunda[0], segunda[1], true)}
          {abajoFilas}
        </tbody>
      </table>
    </div>
  );
}

function Resumen({ r }: { r: ResumenPerio }) {
  const casilla = (t: string, v: string, grave = false) => <span className={`chip ${grave ? "rojo" : ""}`}>{t}: <b>{v}</b></span>;
  return (
    <div className="fila" style={{ flexWrap: "wrap", gap: 6 }} aria-live="polite">
      {casilla("Dientes", String(r.dientes))}
      {casilla("Sitios medidos", String(r.sitios))}
      {casilla("Sondaje medio", r.pdMedia === null ? "—" : `${r.pdMedia} mm`)}
      {casilla("NIC medio", r.nicMedia === null ? "—" : `${r.nicMedia} mm`)}
      {casilla("Sitios ≥ 4 mm", String(r.sitios4), r.sitios4 > 0)}
      {casilla("Sitios ≥ 6 mm", String(r.sitios6), r.sitios6 > 0)}
      {casilla("Sangrado", r.pctSangrado === null ? "—" : `${r.pctSangrado} %`, (r.pctSangrado ?? 0) > 30)}
      {casilla("Placa", r.pctPlaca === null ? "—" : `${r.pctPlaca} %`, (r.pctPlaca ?? 0) > 30)}
      {r.supuracion > 0 && casilla("Supuración", `${r.supuracion} sitios`, true)}
      {r.conMovilidad > 0 && casilla("Con movilidad", String(r.conMovilidad))}
      {r.conFurca > 0 && casilla("Con furca", String(r.conFurca))}
    </div>
  );
}

export function Periodontograma({ paciente }: { paciente: Paciente }) {
  const [lista, setLista] = useState<Registro[] | null>(null);
  const [datos, setDatos] = useState<DatosPerio | null>(null);
  const [viendo, setViendo] = useState<Registro | null>(null);
  const [fecha, setFecha] = useState(hoy());
  const [obs, setObs] = useState("");
  const a = useAccion();
  const url = `/api/pacientes/${paciente.id}/periodontogramas`;
  useEffect(() => { setLista(null); setDatos(null); setViendo(null); api<{ periodontogramas: Registro[] }>("GET", url).then((r) => setLista(r.periodontogramas)).catch(a.setError); }, [paciente.id]);

  const limpio = useMemo(() => (datos ? limpiarPeriodontograma(datos) : null), [datos]);
  const resumen = useMemo(() => (limpio ? resumenPeriodontograma(limpio) : null), [limpio]);
  const problemas = limpio ? problemasPeriodontograma(limpio) : [];
  const editable = !!datos && !viendo;

  const nuevo = () => {
    // los dientes que ya faltaban en el último registro se dejan como ausentes
    const base = periodontogramaVacio();
    const ultimo = lista?.[0];
    if (ultimo) for (const n of Object.keys(base)) if (ultimo.datos[n]?.ausente) base[n].ausente = true;
    setDatos(base); setViendo(null); setObs(""); setFecha(hoy());
  };
  const cambiar = (n: number, f: (d: DatosPerio[string]) => DatosPerio[string]) => setDatos((d) => (d ? { ...d, [String(n)]: f(d[String(n)]) } : d));
  const guardar = async () => {
    const r = await a.correr(() => api("POST", url, { fecha, observaciones: obs, datos: limpio }));
    if (!r) return;
    const x = await api<{ periodontogramas: Registro[] }>("GET", url);
    setLista(x.periodontogramas); setDatos(null);
  };

  if (!lista) return <AvisoError error={a.error} />;
  const mostrado = viendo ? viendo.datos : datos;
  return (
    <Seccion titulo="Periodontograma" acciones={puede("clinico:escribir") && !datos && <button className="btn chico pri" onClick={nuevo}>Nuevo periodontograma</button>}>
      {!mostrado && lista.length === 0 && <p className="muted" style={{ margin: 0 }}>Sin periodontogramas registrados. Cada uno se guarda como un registro nuevo para poder comparar la evolución.</p>}
      {mostrado && (
        <>
          <div className="fila" style={{ flexWrap: "wrap", alignItems: "end" }}>
            {viendo
              ? <b>Periodontograma del {fechaLarga(viendo.fecha)} · {viendo.profesional}</b>
              : <label className="campo" style={{ margin: 0 }}>Fecha<FechaInput max={hoy()} value={fecha} onChange={(e) => setFecha(e.target.value)} /></label>}
            {viendo && <button className="btn chico" onClick={() => setViendo(null)}>Cerrar</button>}
          </div>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            Llena fila por fila: <b>Shift</b> pasa a la casilla siguiente y <b>Enter</b> cambia el valor (en las casillas de números sube de uno en uno y, al pasar del máximo, vuelve a 0; en placa, sangrado y supuración marca o desmarca). También puedes escribir el número. Toca el número del diente para marcarlo ausente; movilidad y furca cambian de 0 a 3 con cada toque. Sondaje de 4 mm o más sale en ámbar y de 6 mm o más en rojo.
            El nivel de inserción (NIC) medio aparece en el resumen de abajo.
            El dibujo de los dientes se va llenando solo: la línea gris es el margen de la encía (recesión) y la línea de color es el fondo del surco (margen + sondaje): <b style={{ color: "#2e9e5b" }}>verde</b> sin hallazgo, <b style={{ color: "#8b5a2b" }}>marrón</b> con sangrado o sondaje de 4 a 5 mm, <b style={{ color: "#d13b3b" }}>rojo</b> con supuración o sondaje de 6 mm o más.
          </p>
          <Arcada dientes={DIENTES_SUPERIORES} datos={mostrado} cambiar={cambiar} bloqueado={!editable} inferior={false} />
          <Arcada dientes={DIENTES_INFERIORES} datos={mostrado} cambiar={cambiar} bloqueado={!editable} inferior />
          <Resumen r={viendo ? viendo.resumen : resumen!} />
          {viendo?.observaciones && <p style={{ margin: 0 }}>{viendo.observaciones}</p>}
          {editable && (
            <>
              <label className="campo">Observaciones (opcional)<textarea rows={2} maxLength={1000} value={obs} onChange={(e) => setObs(e.target.value)} /></label>
              {problemas.length > 0 && <div className="aviso info"><ul>{problemas.map((x) => <li key={x}>{x}</li>)}</ul></div>}
              <AvisoError error={a.error} />
              <div className="fila">
                <button className="btn pri" disabled={a.ocupado || problemas.length > 0} onClick={guardar}>Guardar periodontograma</button>
                <button className="btn" onClick={() => setDatos(null)}>Cancelar</button>
              </div>
              <small className="muted">Al guardarlo queda como registro fijo; si te equivocas, haz uno nuevo.</small>
            </>
          )}
        </>
      )}
      {lista.length > 0 && !datos && (
        <>
          <h3>Registros anteriores</h3>
          <ul className="lista">
            {lista.map((x, i) => {
              const anterior = lista[i + 1];
              const c = anterior ? compararPerio(anterior.resumen, x.resumen) : null;
              return (
                <li key={x.id}>
                  <button onClick={() => setViendo(x)} aria-current={viendo?.id === x.id}>
                    <span><b>{fechaLarga(x.fecha)}</b> · {x.profesional}<br /><span className="muted" style={{ fontSize: 13 }}>{textoResumenPerio(x.resumen)}</span>
                      {c && <><br /><span style={{ fontSize: 13 }}>Frente al anterior: {[signo(c.pdMedia, "mm de sondaje medio"), signo(c.pctSangrado, "puntos de sangrado"), signo(c.sitios4 || null, "sitios ≥ 4 mm")].filter(Boolean).join(" · ") || "sin diferencias"}</span></>}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
      {!mostrado && <AvisoError error={a.error} />}
    </Seccion>
  );
}
